export type PromptDiffOp = "same" | "added" | "removed";

export interface PromptDiffSegment {
  op: PromptDiffOp;
  text: string;
}

/**
 * Split into words AND the whitespace between them, so concatenating the
 * segments reproduces the input byte-for-byte (newlines and indentation in a
 * prompt are meaningful — they must survive the round-trip).
 */
function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((t) => t !== "");
}

/**
 * Guard on the LCS table, which is O(n·m). Only the middle is diffed (the
 * identical head/tail are peeled first), so a minimal prompt edit never comes
 * close; this only trips when a suggestion rewrote the prompt wholesale.
 */
const MIDDLE_TOKEN_LIMIT = 1200;

/** Classic LCS backtrack over tokens, emitting one segment per token. */
function diffTokens(a: string[], b: string[]): PromptDiffSegment[] {
  const m = a.length;
  const n = b.length;
  const width = n + 1;
  const lcs = new Int32Array((m + 1) * width);

  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i * width + j] =
        a[i] === b[j]
          ? lcs[(i + 1) * width + (j + 1)] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + (j + 1)]);
    }
  }

  const out: PromptDiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      out.push({ op: "same", text: a[i] });
      i++;
      j++;
    } else if (lcs[(i + 1) * width + j] >= lcs[i * width + (j + 1)]) {
      out.push({ op: "removed", text: a[i] });
      i++;
    } else {
      out.push({ op: "added", text: b[j] });
      j++;
    }
  }
  while (i < m) out.push({ op: "removed", text: a[i++] });
  while (j < n) out.push({ op: "added", text: b[j++] });
  return out;
}

/** Collapse runs of the same op so the render is a handful of spans, not hundreds. */
function mergeRuns(segments: readonly PromptDiffSegment[]): PromptDiffSegment[] {
  const out: PromptDiffSegment[] = [];
  for (const segment of segments) {
    if (!segment.text) continue;
    const last = out[out.length - 1];
    if (last && last.op === segment.op) last.text += segment.text;
    else out.push({ ...segment });
  }
  return out;
}

/**
 * Word-level diff of two prompt texts.
 *
 * Word granularity (not line) is the point: suggestions are minimal edits to a
 * long prompt, so a line diff would light up a whole paragraph for a
 * one-sentence insert and hide WHERE the change actually landed.
 */
export function diffPromptWords(before: string, after: string): PromptDiffSegment[] {
  const a = tokenize(before);
  const b = tokenize(after);

  // Peel the identical head/tail — a minimal edit leaves a tiny middle to LCS.
  let head = 0;
  while (head < a.length && head < b.length && a[head] === b[head]) head++;
  let tail = 0;
  while (
    tail < a.length - head &&
    tail < b.length - head &&
    a[a.length - 1 - tail] === b[b.length - 1 - tail]
  ) {
    tail++;
  }

  const aMiddle = a.slice(head, a.length - tail);
  const bMiddle = b.slice(head, b.length - tail);

  const middle: PromptDiffSegment[] =
    aMiddle.length > MIDDLE_TOKEN_LIMIT || bMiddle.length > MIDDLE_TOKEN_LIMIT
      ? [
          { op: "removed", text: aMiddle.join("") },
          { op: "added", text: bMiddle.join("") },
        ]
      : diffTokens(aMiddle, bMiddle);

  return mergeRuns([
    { op: "same", text: a.slice(0, head).join("") },
    ...middle,
    { op: "same", text: a.slice(a.length - tail).join("") },
  ]);
}
